import { type FC } from "react";
import { type LinearSubstep } from "../../Linear/models";
import { useDispatch, useSelector } from "react-redux";
import SubstepFrame from "../../Linear/components/SubstepFrame";
import LinearTile from "../../Linear/components/LinearTile";
import { Col, Row } from "../../../common/components/Layout";
import { getKmats } from "../../Linear/redux/selectors";
import { Card } from "../../Linear/components/Card";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import { getTileData, makeRenderTile } from "~/Configurators/common/components/GenericTileSubstep/GenericTileSubstep";
import type { CustomDispatch } from "~/redux/types";
import type { RootState } from "~/redux/store";
import { isTileEnabled, isTileStandard } from "~/Configurators/common/tileUtils";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const RailLocatingFaceSubstep: FC<Props> = ({ def, ...props }) => {
  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={true}
      showError={false}
      errorMessage={""}
      {...props}
    >
      <div className={"w-100 mb-30"}>
        <Row className={"d-flex"}>
          <Col size={4}>
            <SingleGuidewayTile def={def} />
          </Col>
        </Row>
      </div>
    </SubstepFrame>
  );
};
export default RailLocatingFaceSubstep;

interface TileProps {
  def: LinearSubstep;
}

const SingleGuidewayTile = ({ def }: TileProps) => {
  const t = useXmlConfiguratorTranslations().t!;
  const dispatch: CustomDispatch<RootState> = useDispatch();
  const tileData = getTileData(def, dispatch)[0];

  const tileAbove = tileData[0];
  const tileBelow = tileData[1];

  const debugLanguage = isDebugLanguageEnabled();
  const renderTitle = makeRenderTile(debugLanguage, def);
  const kmats = useSelector(getKmats);

  const abovePicked = tileAbove.tileIndex === def.selectedTile;

  const locatingFaceImageUrl = t((abovePicked ? tileAbove : tileBelow).imageUrl);

  return (
    <Card disableHover={true}>
      <div className={"p-30"}>
        <Row>
          <Col size={12}>
            <LinearTile
              title={renderTitle(tileAbove)}
              isDisabled={!isTileEnabled(tileAbove, kmats)}
              isStandard={isTileStandard(tileAbove, kmats)}
              isPicked={abovePicked}
              forceStandardSpace={true}
              onClickHandler={tileAbove.onClick}
            />
          </Col>
        </Row>

        <div className={"my-3"}>
          <img className={"w-100"} src={locatingFaceImageUrl} alt={""} />
        </div>

        {tileBelow && (
          <Row>
            <Col size={12}>
              <LinearTile
                title={renderTitle(tileBelow)}
                isDisabled={!isTileEnabled(tileBelow, kmats)}
                isStandard={isTileStandard(tileBelow, kmats)}
                isPicked={!abovePicked}
                forceStandardSpace={true}
                onClickHandler={tileBelow.onClick}
              />
            </Col>
          </Row>
        )}
      </div>
    </Card>
  );
};
